export const steps = ['finish', 'size', 'legs', 'installation'];
export function parseConfiguration(value) {
  const text = String(value).replace(/×/g, 'x').replace(/\s+/g, ' ').trim();
  const size = text.match(/(\d+(?:\.\d+)?)\s*m?\s*x\s*(\d+(?:\.\d+)?)\s*m?/i);
  return {size: size ? `${size[1]}m × ${size[2]}m` : text, legs: /\b6\s*[- ]?leg/i.test(text) ? '6 legs' : /\b4\s*[- ]?leg/i.test(text) ? '4 legs' : 'Standard frame'};
}
export function prepareVariants(raw) {
  return raw.map(v => ({...v, finish:v.finish || v.option1 || v.options?.[0], configuration:v.configuration || v.option2 || v.options?.[1], installation:v.installation || v.option3 || v.options?.[2], image:v.image || v.featured_image?.src || ''})).filter(v => !/electric|remote|motor|led/i.test(v.configuration)).map(v => ({...v, ...parseConfiguration(v.configuration)}));
}
export function candidates(variants, selection, step, value) {
  const prior = steps.slice(0, steps.indexOf(step));
  return variants.filter(v => v[step] === value && prior.every(key => !selection[key] || v[key] === selection[key]));
}
export function matchVariant(variants, selection) {
  return steps.every(k => selection[k]) ? variants.find(v => steps.every(k => v[k] === selection[k])) : undefined;
}
export function installationCost(variants, variant) {
  const base = variants.find(v => v.finish === variant.finish && v.configuration === variant.configuration && /no|without/i.test(v.installation));
  return base ? Math.max(0, variant.price - base.price) : undefined;
}
function setup(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = 'true';
  const raw = JSON.parse(root.querySelector('[data-pergola-data]').textContent);
  const variants = prepareVariants(raw);
  const selected = Object.fromEntries(steps.map(k => [k, null]));
  const money = v => new Intl.NumberFormat('en-GB', {style:'currency', currency:'GBP'}).format(v / 100);
  const swatches = {'Anthracite':'#424446', 'Light Grey':'#c8c9c6', 'White':'#f7f6f1', 'Wood Effect':'linear-gradient(135deg,#9f7651,#c29a70,#815d3e)', 'Black':'#151515'};
  const allAvailable = variants.some(v => v.available);
  const purchasable = v => v.available || !allAvailable;
  const form = root.querySelector('form');
  const submit = root.querySelector('[data-add]');
  function render() {
    steps.forEach((step, index) => {
      const container = root.querySelector(`[data-options="${step}"]`);
      const values = [...new Set(variants.map(v => v[step]))];
      container.replaceChildren(...values.map(value => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.step = step;
        button.dataset.value = value;
        button.className = 'gp-config__option';
        button.setAttribute('aria-pressed', String(selected[step] === value));
        button.disabled = !candidates(variants, selected, step, value).some(purchasable);
        if (step === 'finish') {
          const swatch = document.createElement('span');
          swatch.className = 'gp-config__swatch';
          swatch.style.background = swatches[value] || '#d8d7d2';
          swatch.setAttribute('aria-hidden', 'true');
          button.append(swatch);
        }
        const text = document.createElement('span');
        text.textContent = value;
        button.append(text);
        return button;
      }));
      root.querySelector(`[data-summary="${step}"]`).textContent = selected[step] || 'Not selected';
      root.querySelector(`[data-step-panel="${step}"]`).classList.toggle('is-complete', Boolean(selected[step]));
      root.querySelector(`[data-progress="${step}"]`).setAttribute('aria-current', steps.find(k => !selected[k]) === step ? 'step' : 'false');
    });
    const variant = matchVariant(variants, selected);
    const base = variants.find(v => ['finish','size','legs'].every(k => selected[k] && v[k] === selected[k]) && v.image) || variants.find(v => v.finish === selected.finish && v.image);
    const image = root.querySelector('[data-preview]');
    if (base) { image.src = base.image; image.alt = `${base.finish} pergola, ${base.size}, ${base.legs}`; }
    root.querySelector('[data-summary="price"]').textContent = variant ? money(variant.price) : 'Select all options';
    const fee = variant ? installationCost(variants, variant) : undefined;
    root.querySelector('[data-summary="installation-cost"]').textContent = fee === undefined ? 'Select installation option' : money(fee);
    root.querySelector('input[name="id"]').value = variant ? variant.id : '';
    const live = root.dataset.purchasable === 'true';
    submit.disabled = !variant || !variant.available || !live;
    submit.textContent = !live ? 'Available at launch' : variant && !variant.available ? 'Currently unavailable' : 'Add to basket';
    const next = steps.find(k => !selected[k]);
    root.querySelector('[data-status]').textContent = next ? `Choose your ${next === 'size' ? 'dimensions' : next === 'legs' ? 'frame layout' : next} to continue.` : variant ? 'Your configuration is selected.' : 'This combination is unavailable.';
  }
  root.addEventListener('click', event => {
    const button = event.target.closest('[data-step][data-value]');
    if (!button || button.disabled) return;
    const index = steps.indexOf(button.dataset.step);
    selected[button.dataset.step] = button.dataset.value;
    steps.slice(index + 1).forEach(key => { selected[key] = null; });
    render();
  });
  form.addEventListener('submit', async event => {
    event.preventDefault();
    const variant = matchVariant(variants, selected);
    if (!variant || !variant.available || root.dataset.purchasable !== 'true') return;
    submit.disabled = true;
    const status = root.querySelector('[data-status]');
    try {
      const routesRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${routesRoot}cart/add.js`, {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({items:[{id:variant.id,quantity:1}]})});
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Could not add this configuration. Please try again.');
      window.location.assign(`${routesRoot}cart`);
    } catch(error) { status.textContent = error.message; submit.disabled = false; }
  });
  render();
}
function initialise() { document.querySelectorAll('[data-gp-configurator]').forEach(setup); }
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise); else initialise();
  document.addEventListener('shopify:section:load', initialise);
}
